import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertCircle } from "lucide-react";

import { api } from "@/lib/api";
import {
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  CardBody,
} from "@/components/ui/Card";

type Status = "loading" | "success" | "error";

export default function MfaResetCancelPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [status, setStatus] = useState<Status>("loading");
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current || !token) return;
    firedRef.current = true;
    api
      .mfaResetCancel({ token })
      .then(() => setStatus("success"))
      .catch(() => setStatus("error"));
  }, [token]);

  if (!token) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-danger-50">
            <AlertCircle className="h-6 w-6 text-danger-500" />
          </div>
          <CardTitle>{t("errors.invalid_or_expired_token")}</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (status === "success") {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-success-50">
            <CheckCircle2 className="h-6 w-6 text-success-500" />
          </div>
          <CardTitle>{t("auth.mfa_reset.cancel_title")}</CardTitle>
          <CardSubtitle>{t("auth.mfa_reset.cancel_subtitle")}</CardSubtitle>
        </CardHeader>
        <CardBody>
          <Link
            to="/login"
            className="text-caption font-medium text-brand-500 hover:underline"
          >
            {t("auth.forgot_password.back_to_login")}
          </Link>
        </CardBody>
      </Card>
    );
  }

  if (status === "error") {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-danger-50">
            <AlertCircle className="h-6 w-6 text-danger-500" />
          </div>
          <CardTitle>{t("errors.generic")}</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  // Loading
  return (
    <Card>
      <CardHeader>
        <div className="mb-2 flex h-12 w-12 items-center justify-center">
          <div
            className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand-500"
            aria-hidden="true"
          />
        </div>
        <CardTitle>{t("common.loading")}</CardTitle>
      </CardHeader>
    </Card>
  );
}
